import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import type { ButtonProps, ButtonVariant } from '../types';
import { colors, controleLarge, radius, spacing, typography } from '../theme/tokens';
import { readableOn } from '../theme/templates';
import { fleche } from '../i18n/sens';

interface VariantStyle {
  background: string;
  textColor?: string; // si absent : contraste auto via readableOn(background)
  borderColor?: string;
}

// secondary = charte : fond blanc, contour gris clair, texte bleu nuit.
// Les variantes pleines laissent readableOn choisir le texte (évite le blanc illisible sur fond clair).
const VARIANTS: Record<ButtonVariant, VariantStyle> = {
  primary: { background: colors.bluePrimary },
  success: { background: colors.green },
  dark: { background: colors.ink },
  secondary: { background: colors.white, textColor: colors.night, borderColor: colors.grey200 },
};

export function Button({
  title,
  onPress,
  variant = 'primary',
  withArrow = false,
  loading = false,
  disabled = false,
  testID,
}: ButtonProps) {
  const v = VARIANTS[variant];
  const isInactive = disabled || loading;
  // Désactivé (hors chargement) : fond neutre + texte gris, pas un bleu délavé.
  const showDisabled = disabled && !loading;
  const background = showDisabled ? colors.disabledBg : v.background;
  const textColor = showDisabled ? colors.disabledText : v.textColor ?? readableOn(v.background);
  const borderColor = showDisabled ? 'transparent' : v.borderColor ?? 'transparent';

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ disabled: isInactive, busy: loading }}
      disabled={isInactive}
      onPress={onPress}
      className="flex-row items-center justify-center"
      style={[
        styles.base,
        controleLarge,
        {
          backgroundColor: background,
          borderColor,
          borderWidth: showDisabled ? 0 : v.borderColor ? 1 : 0,
        },
        loading && styles.inactive,
      ]}
    >
      {loading ? (
        <ActivityIndicator testID={testID ? `${testID}-spinner` : undefined} color={textColor} />
      ) : (
        <View className="flex-row items-center" style={styles.rangee}>
          <Text style={[styles.label, { color: textColor }]}>{title}</Text>
          {withArrow ? <Text style={[styles.arrow, { color: textColor }]}>{fleche('→')}</Text> : null}
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    height: 48,
    borderRadius: radius.md,
    paddingHorizontal: spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inactive: {
    opacity: 0.5,
  },
  label: {
    fontSize: typography.bodyMd.size,
    fontWeight: typography.bodyMd.weight,
    lineHeight: typography.bodyMd.lineHeight,
  },
  arrow: {
    fontSize: typography.bodyMd.size,
    fontWeight: typography.bodyMd.weight,
  },
  // Espace porté par la rangée : sur le web, un texte reçoit dir="auto", et une flèche seule y est lue
  // de gauche à droite — une marge de début tomberait du mauvais côté en arabe.
  rangee: { gap: spacing.sm },
});
